import React from "react";
import 'react-quill/dist/quill.snow.css';
import TestType from "../../types/TestType";
import Overlay from "../Overlay";
import {
    getBoolSelectOptions,
    getBoolSelectVal, hideModal,
    showModalNoOutsideClick
} from "../../util/FormatUtil";
import {TestManagementModal} from "../modals/TestManagementModal";
import {sweetalert} from '../../App';
import TestsAPI from "../../network/TestsAPI";
import SystemAPI from "../../network/SystemAPI";
import AdminAPI from "../../network/AdminAPI";
import {AdminPages} from "./AdminNav";
import FilterCard, {FIELD_TYPE} from "../FilterCard";
import NetworkUtil from "../../network/NetworkUtil";
import {Column} from "../tables/TableBase";
import {Sorter, TableOrder} from "../../sorting/Sorter";
import SimpleTable from "../tables/SimpleTable";
import Parse from "html-react-parser";
import {SampleTestReportModal} from "../modals/SampleTestReportModal";
import InBetweenOverlay from "../InBetweenOverlay";
import ResultsAPI from "../../network/ResultsAPI";
import SpecimenSourceAPI from "../../network/SpecimenSourceAPI";
import { fetchFieldPermissions } from "../../util/FieldPermissionsFetcher";


interface TestManagementState {
    selectedTest?;
    tests: TestType[]
    testDescription?
    name?
    shortName?
    isEnabled?:{label, value}
    showLoading?
    showInBetween?
    resultTypes:{label, value}[]
    allResults
    specimenSources:{label, value}[]
    pageCustomQuestions?
    filter?:{
        SelectedTest
    }
    tableData:{}[]
    direction?:TableOrder
    testQueryResults:TestType[]
    userRoles?: string;
    fieldPermissions?: Record<string, number[]>;
}
export interface PostedTest extends TestType{
    ResultIDs:number[]
    SpecimenIDs:number[]
}

export class TestManagement extends React.Component<any, TestManagementState> {

    constructor(props) {
        super(props);
        this.state = {
            showLoading: false,
            showInBetween: false,
            selectedTest: {} as TestType,
            tests: [], 
            resultTypes:[],
            allResults: [],
            specimenSources:[],
            pageCustomQuestions: [],
            filter: {SelectedTest:[],
            },
            tableData: [] as any,
            direction: 'asc',
            testQueryResults: [] as any,
        }
        this.createOrModifyTest = this.createOrModifyTest.bind(this);
    }


    // Keeps the Test filter dropdown alphabetical on every load, not just the first
    static sortTestsByLabel(tests) {
        return (tests || []).sort((a, b) => String(a.label).localeCompare(String(b.label)));
    }

    componentDidMount() {
        document.title = 'Test Management Page';
        fetchFieldPermissions((state) => this.setState(state as any));
        this.setState({showLoading: true}, () => {
            SystemAPI.getCustomQuestions(AdminPages.Test).then(res =>{
                this.setState({pageCustomQuestions:res.questions})
            })
            SystemAPI.getAllTests().then(data => {
                this.setState({tests: TestManagement.sortTestsByLabel(data)})
            })
            AdminAPI.getAllEnabledResultTypes().then(data => {
                this.setState({resultTypes: data} as any)
            })
            SpecimenSourceAPI.getEnabledSpecimenSourceData().then(response => {
              let mapped = response.data.specimenSources.map(source => { return {label: source.Description, value: source.ID}})
                this.setState({specimenSources: mapped})
            })
            ResultsAPI.getAllResultsData().then(data => {
                this.setState({allResults: data.data.results})
            })
            this.queryTest()
        })
    }

    queryTest() {
        this.setState({showLoading:true}, async () => {
            try {
                let results = await TestsAPI.filterTest({
                    filter: {SelectedTest: this.state.filter.SelectedTest,
                        }
                })
                if(results.data.length < 1){
                    this.setState({showLoading: false, tableData: [], testQueryResults: null})
                    return sweetalert.fire({title: '', text: 'No Tests returned', icon: 'info'})
                }

                if(!results.success){
                    return sweetalert.fire({title: '', text: 'Unable to filter Test data at this time', icon: 'error'})
                }
                this.setState({
                    tableData: results.data,
                    testQueryResults: results.data,
                    showLoading: false,
                })
            } catch (e) {
                console.log(e)
                this.setState({showLoading: false})
            }
        })
    }


    createOrModifyTest(test:TestType){


        this.setState({showLoading:true}, async() => {
            if(test.ID === null){
                try {
                    let result = await TestsAPI.newTest(test);
                    if(result.success){
                        sweetalert.fire({icon: 'success', title: '', text: 'Test saved'}).then(()=>{
                            this.setState({
                                selectedTest: {
                                    Name: '',
                                    ShortName: '',
                                    IsEnabled: null,
                                    ReportDescription: '',
                                    TestOrderedCode: '',
                                    TestOrderedDescription: '',
                                    TestPerformedCode: '',
                                    TestPerformedDescription: '',
                                    LowRangeStart: null,
                                    LowRangeEnd : null,
                                    ModerateRangeStart : null,
                                    ModerateRangeEnd : null,
                                    HighRangeStart : null,
                                    HighRangeEnd : null,
                                    CustomFieldData: null,
                                    PrescriptionRecommendationThreshold: null,
                                    IncludeInReporting: null,
                                } as TestType
                            }, () => {
                                SystemAPI.getAllTests().then(data => {
                                    this.setState({tests: TestManagement.sortTestsByLabel(data), showLoading: false})
                                })
                                this.queryTest();
                            });
                        });
                    }
                    else{
                        sweetalert.fire({icon: 'error', title: '', text: result.reason});
                        this.setState({showLoading: false})
                    }
                }catch (e) {
                    console.log(e)
                    this.setState({showLoading: false})
                }
            } else {
                try{
                    let result = await TestsAPI.editTest(test)
                    if(result.success){
                        sweetalert.fire({icon: 'success', title: '', text: 'Test saved'}).then(()=>{
                            this.setState({
                                selectedTest: {
                                    ID: null,
                                    Name: '',
                                    ShortName: '',
                                    IsEnabled: null,
                                    ReportDescription: '',
                                    TestOrderedCode: '',
                                    TestOrderedDescription: '',
                                    TestPerformedCode: '',
                                    TestPerformedDescription: '',
                                    LowRangeStart: null,
                                    LowRangeEnd : null,
                                    ModerateRangeStart : null,
                                    ModerateRangeEnd : null,
                                    HighRangeStart : null,
                                    HighRangeEnd : null,
                                    CustomFieldData: null,
                                    PrescriptionRecommendationThreshold: null,
                                    IncludeInReporting: null,
                                } as TestType
                            }, () => {
                                SystemAPI.getAllTests().then(data => {
                                    this.setState({tests: TestManagement.sortTestsByLabel(data), showLoading: false})
                                })
                                this.queryTest();
                            });
                        });
                    }else{
                        sweetalert.fire({icon: 'error', title: '', text: result.reason});
                        this.setState({showLoading:false})
                    }
                }
                catch (e) {
                    console.log(e)
                    this.setState({showLoading:false})
                }
            }
        });
    }

    handleExportToCSV(){
        this.setState({showLoading: true}, async () =>{
            await NetworkUtil.downloadCSV(
                "/api/admin/tests/csv",
                "Test Management Data.xlsx",
                {filter: this.state.filter}
            )
            this.setState({showLoading: false})
        });
    }

    useSorter(col:Column){
        let sorter = new Sorter<any>()

        this.setState({tableData: sorter.sortByKey(this.state.tableData, col.key as keyof any,this.state.direction), direction: this.state.direction === 'asc' ? 'desc' : 'asc'});
    }

    renderTestMgmtFilterFields(){
        return (
            <FilterCard fields={[
                {
                    label: "Test Name",
                    "key": "SelectedTest",
                    type: FIELD_TYPE.SELECT,
                    options: this.state.tests,
                    isMapped: true,
                    isMulti: true,
                    textType: 'text',
                    isFilter: true
                }, 
            ]} filterChanged={(e)=> this.setState({ filter: e })} />
        )
    }

    getTestDetails(val){

        this.setState({showLoading: true}, async () => {
            try {
                let result = await TestsAPI.queryTest({ID: val.ID})

                if (result.success) {
                    let test = result.test
                    this.setState({selectedTest: {
                        ID: test.ID ? test.ID : null,
                        CreatedDate: test.CreatedDate ? test.CreatedDate : null,
                        Name: test.Name ? test.Name : "",
                        ShortName: test.ShortName ? test.ShortName : "",
                        ReportDescription: test.ReportDescription ? test.ReportDescription : "",
                        IsEnabled: test.IsEnabled !== null ? test.IsEnabled : null,
                        TestPerformedCode: test.TestPerformedCode ? test.TestPerformedCode : "",
                        TestPerformedDescription: test.TestPerformedDescription ? test.TestPerformedDescription : "",
                        TestOrderedCode: test.TestOrderedCode ? test.TestOrderedCode : "",
                        TestOrderedDescription: test.TestOrderedDescription ? test.TestOrderedDescription : "",
                        LowRangeStart: test.LowRangeStart ? test.LowRangeStart : null,
                        LowRangeEnd: test.LowRangeEnd ? test.LowRangeEnd : null,
                        ModerateRangeStart: test.ModerateRangeStart ? test.ModerateRangeStart : null,
                        ModerateRangeEnd: test.ModerateRangeEnd ? test.ModerateRangeEnd : null,
                        HighRangeStart: test.HighRangeStart ? test.HighRangeStart : null,
                        HighRangeEnd: test.HighRangeEnd ? test.HighRangeEnd : null,
                        CustomFieldData: test.CustomFieldData ? test.CustomFieldData : null,
                        PrescriptionRecommendationThreshold: test.PrescriptionRecommendationThreshold ? test.PrescriptionRecommendationThreshold : null,
                        IncludeInReporting: test.IncludeInReporting !==null ? test.IncludeInReporting : null,
                        allowedSources: test.ValidSpecimenSources ? this.state.specimenSources.filter(s => test.ValidSpecimenSources.map(validSource => validSource.ID).includes(s.value)) : [],
                        allowedResults: test.ValidResults ? this.state.resultTypes.filter(s => test.ValidResults.map(validSource => validSource.ID).includes(s.value)) : [],
                    },
                    showLoading: false}, () => showModalNoOutsideClick(TestManagementModal.ID))
                }
            } catch (e) {
                console.error(e)
                this.setState({showLoading: false})
            }
        })

    }

    render() {

        // console.log('TestMgmt state', this.state)

        return (<React.Fragment>
            <Overlay show_loading={this.state.showLoading}/>
            <InBetweenOverlay showInBetween={this.state.showInBetween} />
            <TestManagementModal
                selectedTest={this.state.selectedTest}
                resultTypes={this.state.resultTypes}
                allResults={this.state.allResults}
                specimenSources={this.state.specimenSources}
                onSubmit={(test:TestType) => {
                    hideModal(TestManagementModal.ID);
                    this.createOrModifyTest(test);
                }}
                pageCustomQuestions={this.state.pageCustomQuestions}
                userRoles={this.state.userRoles}
                fieldPermissions={this.state.fieldPermissions}
            />
            <SampleTestReportModal selectedTest={this.state.selectedTest} handleShowInBetween={() => this.setState({showInBetween: false})} />
            <div className="container-fluid ">
                <div className={"row"}>
                    <div className="col-12 col-lg-8 col-xl-5 pt-2">
                        <main id="main-content" tabIndex={-1} aria-label="Test Management">
                            <div className="card mb-2">
                                <div className="card-header verlag-bold">
                                    <h4>Test Management</h4>
                                </div>
                                <div className="card-body">
                                    {this.renderTestMgmtFilterFields()}
                                </div>
                                <div className="card-footer">
                                    <button className={"btn immySubmitButtonOutline"}
                                            onClick={ () => this.queryTest()}
                                    >Search</button>
                                    <button className="btn btn-outline-success float-right"
                                            onClick={()=> this.setState({
                                                selectedTest: {
                                                    ID: null,
                                                    Name: '',
                                                    ShortName: '',
                                                    IsEnabled: null,
                                                    ReportDescription: '',
                                                    TestOrderedCode: '',
                                                    TestOrderedDescription: '',
                                                    TestPerformedCode: '',
                                                    TestPerformedDescription: '',
                                                    LowRangeStart: null,
                                                    LowRangeEnd: null,
                                                    ModerateRangeStart: null,
                                                    ModerateRangeEnd: null,
                                                    HighRangeStart: null,
                                                    HighRangeEnd: null,
                                                    CustomFieldData: null,
                                                    PrescriptionRecommendationThreshold: null,
                                                    IncludeInReporting: null
                                                } as TestType
                                            },()=>{
                                                showModalNoOutsideClick(TestManagementModal.ID)
                                            })}
                                    >Create New</button>
                                </div>
                            </div>
                        </main>
                    </div>
                    
                    {
                        this.state.tableData &&
                        this.state.tableData.length > 0 &&

                        <div className="col-md-12 pt-2">
                            <div className="card mt-2 mb-5">
                                <div className="card-header verlag-bold">
                                    <h4 className="text-center text-md-left">Tests
                                    <section className="tableHeaderSection float-md-right d-flex justify-content-around">
                                        <h4 className={'float-md-right'} aria-label="Total Records" role="alert">Total: {this.state.testQueryResults.length}</h4>
                                    </section>
                                    <button className={"d-none d-md-inline btn btn-outline-primary ml-3"}
                                            onClick={() => this.handleExportToCSV()}
                                    >Export to CSV
                                    </button>
                                    </h4>
                                </div>
                                <div className="card-body p-0 m-0 table-responsive">
                                    <SimpleTable table_style='tableFixHead' columns={[
                                        {
                                            label: "Name", key: "Name",
                                            rawFormat: (val) => {
                                                return <a href={"#!"} className={'tableNameLinkColor'} onClick={() => {
                                                    this.getTestDetails(val)
                                                }}>{val.Name}</a>
                                            },
                                            popoverText: "Click to sort by Name"
                                        },
                                        {
                                            label: "Short Name", key: "ShortName",
                                            popoverText: "Click to sort by Short Name"
                                        },
                                        {
                                            label: "Is Enabled?", key: "IsEnabled",
                                            popoverText: "Click to sort by Is Enabled",
                                            rawFormat: (val) => {
                                                let string = getBoolSelectVal(val.IsEnabled)
                                                return <p>{string.label}</p>
                                            }
                                        },
                                        // {
                                        //     label: "Sample Report", key: "",
                                        //     rawFormat: (val) => {
                                        //         return <a  href={"#top"} className={'btn btn-success'} style={{color: "white"}} onClick={() => {
                                        //             this.setState({ selectedTest: val },
                                        //                 () => showModalNoOutsideClick(SampleTestReportModal.ID))
                                        //         }}>View Sample Report</a>
                                        //     },
                                        // },
                                    ]} table_data={this.state.tableData} columnClickedCallback={(col =>{
                                        this.useSorter(col);
                                    })} />
                                </div>
                            </div>
                        </div>
                    }

                </div>
            </div>
        </React.Fragment>)
    }
}